import type React from "react";
import { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  MenuItem,
  FormControl,
  InputLabel,
  Typography,
  Avatar,
  IconButton,
  Divider,
  OutlinedInput,
  Select,
  Chip,
  FormControlLabel,
  Switch,
  type SelectChangeEvent,
  CircularProgress,
  Alert,
} from "@mui/material";
import { CloudUpload, Close } from "@mui/icons-material";
import { MuiTelInputInfo, matchIsValidTel } from "mui-tel-input";
import { toast } from "react-toastify";
import CustomTextField from "../../components/common/Textfield";
import Button from "../../components/common/Button";
import PhoneNumberField from "../../components/common/PhoneNumberField";
import type { User } from "../../store/slice/user-management/types";

// const userRoles = [
//   {
//     title: "Admin",
//     value: "admin"
//   },
//   {
//     title: "Staff",
//     value: "staff"
//   },
//   {
//     title: "Crew",
//     value: "crew"
//   },
//   {
//     title: "Client",
//     value: "client"
//   },
//   {
//     title: "Funeral Director",
//     value: "funeral_director"
//   }
// ]
// //  client, funeral_director, staff, admin, crew

type EditUserDialogProps = {
  open: boolean;
  onClose: () => void;
  onSave: (formData: FormData) => void;
  selectedUser: User;
};

export default function EditUserDialog({
  open,
  onClose,
  onSave,
  selectedUser,
}: EditUserDialogProps) {
  /* -------------------- STATE -------------------- */
  const [formData, setFormData] = useState<User>({...selectedUser});
  const [previewUrl, setPreviewUrl] = useState<string | null>(selectedUser.profile_picture_url || null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [loading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const rolesList = [
    { value: "client", label: "Client" },
    { value: "funeral_director", label: "Funeral Director" },
    { value: "staff", label: "Staff" },
    { value: "admin", label: "Admin" },
    { value: "crew", label: "Crew" },
    { value: "dev", label: "Dev" },
    { value: "referral", label: "Referral" },
  ];

  const roleColorMap: Record<string, "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning"> = {
    admin: "primary",
    staff: "info",
    crew: "success",
    client: "default",
    funeral_director: "secondary",
    dev: "warning",
    referral: "warning",
  };

  /* -------------------- SYNC USER -------------------- */
  useEffect(() => {
    setFormData({...selectedUser});
    setPreviewUrl(selectedUser.profile_picture_url || null);
    setSelectedFile(null);
  }, [selectedUser]);

  useEffect(() => {
    if (selectedFile) {
      const url = URL.createObjectURL(selectedFile);
      setPreviewUrl(url);
      return () => URL.revokeObjectURL(url);
    }
  }, [selectedFile]);
  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] || null;
    if (file) {
      // Validate file type
      const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'];
      if (!validTypes.includes(file.type)) {
        alert('Please select a valid image file (JPG, PNG, or GIF).');
        return;
      }
      // Validate file size (5MB max)
      const maxSize = 5 * 1024 * 1024;
      if (file.size > maxSize) {
        alert('File size too large. Maximum size is 5MB.');
        return;
      }
      setSelectedFile(file);
    }
  };

  /* -------------------- HANDLERS -------------------- */
  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleRoleChange = (e: SelectChangeEvent<string>) => {
    setFormData((prev) => ({
      ...prev,
      role: e.target.value,
    }));
  };

  const handleStatusToggle = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prev) => ({
      ...prev,
      is_active: e.target.checked,
    }));
  };

  const handle2FAToggle = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prev) => ({
      ...prev,
      is_2fa_enabled: e.target.checked,
    }));
  };

  const handlePhoneChange = (_value: string, info: MuiTelInputInfo) => {
    setFormData((prev) => ({
      ...prev,
      phone_number: `+${info.countryCallingCode}${info.nationalNumber}`,
    }));
  };

  /* -------------------- SAVE -------------------- */
  const buildUserFormData = () => {
    const fd = new FormData();
    const append = (key: string, value: unknown) => {
      if (value !== undefined && value !== null && value !== "") {
        if (typeof value === "boolean" || typeof value === "number") {
          fd.append(key, value.toString());
        } else if (value instanceof Blob) {
          fd.append(key, value);
        } else {
          fd.append(key, String(value));
        }
      }
    };
    append('first_name', formData.first_name);
    append('last_name', formData.last_name);
    append('email', formData.email);
    append('phone_number', formData.phone_number);
    append('is_active', formData.is_active);
    append('user_type', formData.user_type);
    append('role', formData.role);
    append('organization', formData.organization);
    append('is_verified', formData.is_verified);
    append('country', formData.country);
    append('user_timezone', formData.user_timezone);
    append('company', formData.company ? String(formData.company.id) : '');
    append('company_branch', formData.company_branch);
    append('is_2fa_enabled', formData.is_2fa_enabled);
    // Always append profile_picture if present
    if (selectedFile) {
      fd.append('profile_picture', selectedFile, selectedFile.name);
    }
    return fd;
  };

  const handleSave = () => {
    if (!formData.phone_number) {
      toast.error("Please provide a phone number for this user.");
      return;
    }

    if (!matchIsValidTel(formData.phone_number)) {
      toast.error("Please enter a valid phone number with country code.");
      return;
    }

    setIsSubmitting(true);
    const fd = buildUserFormData();
    onSave(fd);
    setIsSubmitting(false);
  };

  const handleClose = () => {
    setError(null);
    onClose();
  };

  /* =========================
     RENDER
  ========================= */
  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between">
          <Typography variant="h6">Edit User</Typography>
          <IconButton onClick={handleClose}>
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {loading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : (
          <Box sx={{ gap: 4 }}>

            {/* Avatar upload and preview */}
            <Box textAlign="center" mb={3}>
              <input
                accept="image/*"
                style={{ display: "none" }}
                id="edit-file-upload"
                type="file"
                onChange={handleFileUpload}
              />
              <label htmlFor="edit-file-upload">
                <Avatar
                  src={previewUrl || undefined}
                  sx={{ width: 96, height: 96, mx: "auto", cursor: "pointer" }}
                >
                  <CloudUpload />
                </Avatar>
              </label>
              <Typography variant="caption" color="text.secondary">
                Click avatar to upload/change profile picture
              </Typography>
              {selectedFile && (
                <Typography variant="caption" color="text.secondary">
                  Selected: {selectedFile.name}
                </Typography>
              )}
            </Box>

            <CustomTextField
              label="First Name"
              name="first_name"
              value={formData.first_name || ""}
              onChange={handleInputChange}
              fullWidth
              sx={{
                marginBottom: 3
              }}
            />
            <CustomTextField
              label="Last Name"
              name="last_name"
              value={formData.last_name || ""}
              onChange={handleInputChange}
              fullWidth
              sx={{
                marginBottom: 3
              }}
            />
            <CustomTextField
              label="Email"
              name="email"
              value={formData.email || ""}
              onChange={handleInputChange}
              fullWidth
              sx={{
                marginBottom: 3
              }}
            />
            <PhoneNumberField
              fullWidth
              label="Phone Number"
              placeholder="Phone Number. e.g. +263712345678"
              value={formData.phone_number || ""}
              onChange={handlePhoneChange}
              defaultCountry="ZW"
              variant="outlined"
              sx={{ marginBottom: 3 }}
            />
           
            {/* <CustomTextField
              label="Country"
              name="country"
              value={formData.country || ""}
              onChange={handleInputChange}
              fullWidth
            /> */}

            <FormControl fullWidth>
              <InputLabel>Role</InputLabel>
              <Select
                value={formData.role}
                onChange={handleRoleChange}
                input={<OutlinedInput label="Role" />}
                renderValue={(selected) => {
                  const val = String(selected || "").toLowerCase();
                  const label = val === "funeral_director" ? "Funeral Director" : val.replace(/_/g, " ");
                  const color = roleColorMap[val] || "default";
                  return <Chip label={label} color={color} size="small" />;
                }}
              >
                {rolesList.map((r) => (
                  <MenuItem key={r.value} value={r.value}>
                    {r.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Divider sx={{ my: 2 }} />

            <FormControlLabel
              control={
                <Switch
                  checked={formData.is_active}
                  onChange={handleStatusToggle}
                />
              }
              label="Active"
            />

            <FormControlLabel
              control={
                <Switch
                  checked={formData.is_2fa_enabled}
                  onChange={handle2FAToggle}
                />
              }
              label="Two-Factor Authentication"
            />
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button variant="outlined" onClick={handleClose}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={loading || isSubmitting}
        >
          Save Changes
        </Button>
      </DialogActions>
    </Dialog>
  );
}
